import { useState } from "react";
import { Button, Checkbox, Empty, Tag, Tooltip } from "antd";
import { ArrowRight, CheckCircle, Copy } from "react-feather";
import copyToClipboard from "../../../utils/copyToClipboard";

const Step1SelectItems = ({
  orders = [],
  selectedOrderIds = [],
  onToggleOrder,
  onSelectAll,
  onMarkArrived,
  onProceedToPacking,
  onDirectCreateShipping,
  creatingShipping = false,
  renderOrderItems,
  searchTerm = "",
  t,
}) => {
  const [filterMode, setFilterMode] = useState("all"); // "all" | "arrived" | "transit"

  if (!orders.length) {
    return (
      <div style={{ padding: "40px 16px", textAlign: "center" }}>
        <Empty
          description={t(
            "stepper.noUnassignedOrders",
            "No unassigned 1688 orders to pack.",
          )}
        />
      </div>
    );
  }

  const selectedArrivedOrders = selectedOrderIds.filter((id) => {
    const o = orders.find((ord) => ord.id === id);
    return o?.status?.toLowerCase() === "arrived";
  });

  const arrivedOrdersList = orders.filter(
    (o) => o.status?.toLowerCase() === "arrived",
  );
  const inTransitOrdersList = orders.filter(
    (o) => o.status?.toLowerCase() !== "arrived" && o.status !== "canceled" && o.status !== "cancelled",
  );

  const displayedOrders = orders.filter((o) => {
    if (filterMode === "arrived") return o.status?.toLowerCase() === "arrived";
    if (filterMode === "transit") return o.status?.toLowerCase() !== "arrived" && o.status !== "canceled" && o.status !== "cancelled";
    return true;
  });

  const allSelected =
    orders.length > 0 && selectedOrderIds.length === orders.length;
  const indeterminate = selectedOrderIds.length > 0 && !allSelected;

  return (
    <div>
      {/* Top Sticky Toolbar */}
      <div
        style={{
          position: "sticky",
          top: "0px",
          zIndex: 9,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "18px",
          padding: "14px 18px",
          backgroundColor: "rgba(248, 250, 252, 0.95)",
          backdropFilter: "blur(8px)",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
          <Checkbox
            indeterminate={indeterminate}
            checked={allSelected}
            onChange={(e) => onSelectAll?.(e.target.checked, orders)}
          >
            <span style={{ fontWeight: 600, fontSize: "14px", color: "#1e293b" }}>
              {t("orderDetails.selectAll", "Select All")}
            </span>
          </Checkbox>

          {/* Quick Filter Chips */}
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            <button
              type="button"
              onClick={() => setFilterMode("all")}
              style={{
                padding: "3px 10px",
                fontSize: "12px",
                fontWeight: filterMode === "all" ? 600 : 500,
                borderRadius: "999px",
                border: "none",
                cursor: "pointer",
                backgroundColor: filterMode === "all" ? "#1e293b" : "#e2e8f0",
                color: filterMode === "all" ? "#ffffff" : "#475569",
                transition: "all 0.15s ease",
              }}
            >
              {t("stepper.filterAll", "All")} ({orders.length})
            </button>

            <button
              type="button"
              onClick={() => setFilterMode("arrived")}
              style={{
                padding: "3px 10px",
                fontSize: "12px",
                fontWeight: filterMode === "arrived" ? 600 : 500,
                borderRadius: "999px",
                border: "none",
                cursor: "pointer",
                backgroundColor: filterMode === "arrived" ? "#10b981" : "#dcfce7",
                color: filterMode === "arrived" ? "#ffffff" : "#166534",
                transition: "all 0.15s ease",
              }}
            >
              ✓ {t("stepper.filterArrived", "Ready to Pack")} ({arrivedOrdersList.length})
            </button>

            <button
              type="button"
              onClick={() => setFilterMode("transit")}
              style={{
                padding: "3px 10px",
                fontSize: "12px",
                fontWeight: filterMode === "transit" ? 600 : 500,
                borderRadius: "999px",
                border: "none",
                cursor: "pointer",
                backgroundColor: filterMode === "transit" ? "#f59e0b" : "#fef3c7",
                color: filterMode === "transit" ? "#ffffff" : "#92400e",
                transition: "all 0.15s ease",
              }}
            >
              ⏳ {t("stepper.filterInTransit", "In Transit")} ({inTransitOrdersList.length})
            </button>
          </div>

          <span style={{ fontSize: "13px", color: "#64748b" }}>
            {selectedOrderIds.length} / {orders.length}{" "}
            {t("stepper.ordersSelected", "orders selected")}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {onDirectCreateShipping && (
            <Button
              type="default"
              disabled={!selectedOrderIds.length || creatingShipping}
              loading={creatingShipping}
              onClick={onDirectCreateShipping}
              style={{ borderRadius: "8px" }}
            >
              {t("orderDetails.createShippingOrder", "Create Shipment (Direct)")}
            </Button>
          )}

          <Tooltip
            title={
              selectedOrderIds.length > 0 && !selectedArrivedOrders.length
                ? t(
                    "orderDetails.onlyArrivedCanPack",
                    "Only orders with status 'Arrived' can be packed into a physical box.",
                  )
                : ""
            }
          >
            <Button
              type="primary"
              size="middle"
              disabled={!selectedArrivedOrders.length}
              onClick={onProceedToPacking}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: 600,
                borderRadius: "8px",
                background: selectedArrivedOrders.length
                  ? "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)"
                  : undefined,
                boxShadow: selectedArrivedOrders.length
                  ? "0 4px 14px rgba(37, 99, 235, 0.35)"
                  : "none",
                border: "none",
              }}
            >
              <span>
                {t("stepper.proceedToPack", "Proceed to Pack Boxes")}
                {selectedArrivedOrders.length > 0 &&
                  ` (${selectedArrivedOrders.length})`}
              </span>
              <ArrowRight size={15} />
            </Button>
          </Tooltip>
        </div>
      </div>

      {/* Orders Grid */}
      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
        }}
      >
        {displayedOrders.map((order) => {
          const isArrived = order.status?.toLowerCase() === "arrived";
          const isCanceled =
            order.status === "canceled" || order.status === "cancelled";
          const isChecked = selectedOrderIds.includes(order.id);

          return (
            <div
              key={order.id}
              onClick={() => onToggleOrder?.(order.id, !isChecked)}
              style={{
                border: isChecked ? "2px solid #2563eb" : "1px solid #e2e8f0",
                borderLeft: isArrived
                  ? "5px solid #10b981"
                  : isCanceled
                  ? "5px solid #ef4444"
                  : "5px solid #f59e0b",
                borderRadius: "12px",
                backgroundColor: isChecked
                  ? "#f8faff"
                  : isCanceled
                  ? "#fff1f2"
                  : "#ffffff",
                boxShadow: isChecked
                  ? "0 6px 18px rgba(37, 99, 235, 0.14)"
                  : "0 2px 8px rgba(0,0,0,0.04)",
                overflow: "hidden",
                transition: "all 0.2s ease",
                cursor: "pointer",
                transform: isChecked ? "translateY(-2px)" : "none",
              }}
            >
              {/* Card Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 16px",
                  borderBottom: "1px solid #f1f5f9",
                  backgroundColor: isChecked ? "#eff6ff" : "#f8fafc",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      marginBottom: "6px",
                    }}
                  >
                    <Checkbox
                      checked={isChecked}
                      onChange={(e) => {
                        e.stopPropagation();
                        onToggleOrder?.(order.id, e.target.checked);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <h4
                      style={{
                        margin: 0,
                        cursor: order.id ? "pointer" : "default",
                        userSelect: "none",
                        fontSize: "15px",
                        fontWeight: 700,
                        color: isChecked ? "#1d4ed8" : "#1e293b",
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        order.id &&
                          copyToClipboard(String(order.id), {
                            successMessage: t("copy.orderIdCopied", {
                              defaultValue: "Order ID copied",
                            }),
                          });
                      }}
                      title={t("copy.doubleClickToCopy", {
                        defaultValue: "Double-click to copy",
                      })}
                    >
                      {t("orderDetails.alibabaOrder", {
                        id: order.id,
                        defaultValue: `1688 Order ${order.id}`,
                      })}
                    </h4>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "3px",
                      paddingLeft: "26px",
                    }}
                  >
                    <p style={{ margin: 0, fontSize: "12px" }}>
                      <strong style={{ color: "#475569" }}>
                        {t("orderDetails.trackingNumber")}:
                      </strong>{" "}
                      {!order.tracking_number ? (
                        <span style={{ color: "#94a3b8" }}>
                          {t("table.common.na")}
                        </span>
                      ) : (
                        <span
                          style={{
                            cursor: "pointer",
                            fontWeight: 500,
                            color: "#0f172a",
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(order.tracking_number, {
                              successMessage: t("copy.trackingCopied", {
                                defaultValue: "Tracking number copied",
                              }),
                            });
                          }}
                        >
                          {order.tracking_number}
                        </span>
                      )}
                    </p>

                    <p style={{ margin: 0, fontSize: "12px" }}>
                      <strong style={{ color: "#475569" }}>
                        {t("orderDetails.status")}:
                      </strong>{" "}
                      {isArrived ? (
                        <Tag color="success" style={{ margin: 0, fontWeight: 600 }}>
                          {t("order.status.arrived", "Arrived")}
                        </Tag>
                      ) : isCanceled ? (
                        <Tag color="error" style={{ margin: 0, fontWeight: 600 }}>
                          {t("order.status.canceled", "Canceled")}
                        </Tag>
                      ) : (
                        <Tag color="warning" style={{ margin: 0, fontWeight: 600 }}>
                          {order.status
                            ? t(`order.status.${order.status.toLowerCase()}`, order.status)
                            : t("order.status.pending", "Pending")}
                        </Tag>
                      )}
                    </p>

                    {order.logistics_code && (
                      <p style={{ margin: 0, fontSize: "12px" }}>
                        <strong style={{ color: "#475569" }}>
                          {t("orderDetails.logisticCode", "Logistic Code")}:
                        </strong>{" "}
                        <span style={{ color: "#64748b" }}>
                          {order.logistics_code}
                        </span>
                      </p>
                    )}
                  </div>
                </div>

                {!isArrived && !isCanceled && (
                  <div
                    className="d-flex flex-column align-items-end"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => onMarkArrived?.(order.id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        fontSize: "12px",
                        borderRadius: "6px",
                      }}
                    >
                      <CheckCircle size={13} style={{ marginRight: 5 }} />
                      {t("orderDetails.markArrived", "Mark Arrived")}
                    </Button>
                  </div>
                )}
              </div>

              {/* Card Body with items */}
              <div style={{ padding: "14px 16px" }}>
                {renderOrderItems?.(order.order_items, searchTerm)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Step1SelectItems;
